import { marked } from 'marked';

import {
  isDev,
  isExperimental,
  isStaging,
  isStagingBuildWithProductionBackend,
  worktreeId,
} from '@/env';
import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';

import { inBrowser } from './capabilities';
import { writeClipboardText } from './clipboard';

export const handleCopyText = async (text: string, copyAsHTML = true) => {
  if (!copyAsHTML) {
    await navigator.clipboard.writeText(text);
    return;
  }

  let html: string;
  try {
    html = await marked(text);
  } catch (error) {
    logError(
      error,
      'Failed to build formatted text, falling back to plain text'
    );
    await navigator.clipboard.writeText(text);
    return;
  }

  await writeClipboardText(text, html);
};

export const handleMailTo = (email: string, subject: string, body: string) => {
  const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  try {
    handleExternalUrlClick(mailtoLink);
  } catch (error) {
    logError(error, 'Failed to open mail client');
  }
};

export const handleExternalUrlClick = (
  url: string,
  options?: { forceInBrowser?: boolean }
): void => {
  if (inBrowser()) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  electronApis.system.openExternal(url, options);
};

export type BrowserType = 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Other';

export const getBrowser = (): BrowserType => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Edg')) {
    return 'Edge';
  } else if (userAgent.includes('Chrome') && !userAgent.includes('OPR')) {
    return 'Chrome';
  } else if (userAgent.includes('Firefox')) {
    return 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    return 'Safari';
  } else {
    return 'Other';
  }
};

export const getDeepLinkProtocol = () => {
  let baseProtocol: string;
  if (isDev) {
    baseProtocol = 'little-bird-dev';
  } else if (isExperimental) {
    baseProtocol = 'little-bird-experimental';
  } else if (isStaging || isStagingBuildWithProductionBackend) {
    baseProtocol = 'little-bird-staging';
  } else {
    baseProtocol = 'little-bird';
  }

  return worktreeId ? `${baseProtocol}-${worktreeId}` : baseProtocol;
};
